import { defineStore } from 'pinia'
import { ref } from 'vue'
import { platformChatApi, type PlatformLinkState } from '../api/platformChat'

/**
 * Where the logo of a LINE / Messenger / Instagram chat goes (lib/platformChat.ts): one GET …/platform-link per such
 * chat that is opened, shared by the chat header (components/chat/PlatformLink.vue) and the customer panel
 * (side/PlatformChatCard.vue). Comments never come here (their logo keeps linking to the server redirect).
 */
export const usePlatformChat = defineStore('platformChat', () => {
  /** conversation id → state · 'error' = could not ask */
  const states = ref<Record<string, PlatformLinkState | 'error'>>({})
  const loading = new Set<string>()

  async function ensure(convId: string, force = false): Promise<void> {
    if (!convId || loading.has(convId) || (!force && states.value[convId] && states.value[convId] !== 'error')) return
    loading.add(convId)
    try {
      states.value[convId] = await platformChatApi().state(convId)
    } catch {
      states.value[convId] = 'error'
    } finally {
      loading.delete(convId)
    }
  }

  /** "ผูกลิงก์แชทใน LINE OA / Meta Business Suite": null = done, else the Thai reason from the server. */
  async function link(convId: string, url: string): Promise<string | null> {
    try {
      states.value[convId] = await platformChatApi().link(convId, url)
      return null
    } catch (e) {
      return e instanceof Error ? e.message : String(e)
    }
  }

  async function unlink(convId: string): Promise<string | null> {
    try {
      states.value[convId] = await platformChatApi().unlink(convId)
      return null
    } catch (e) {
      return e instanceof Error ? e.message : String(e)
    }
  }

  /** The owner changed a LINE OA's link (ตั้งค่า › ช่องทางที่เชื่อม): every chat asks again when it is next shown. */
  function invalidate(): void {
    states.value = {}
  }

  return { states, ensure, link, unlink, invalidate }
})
